import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Sparkles, ArrowRight, HelpCircle, BookMarked, Feather, ChevronUp, ChevronDown } from "lucide-react";
import { JourneyIndicator } from "../components/JourneyIndicator";
import { ContinueMyStory } from "../components/ContinueMyStory";

const PERIODS = ["My Beginnings", "Childhood", "Growing Up", "Love & Family", "Work & Adventure", "Turning Points", "The Wider World", "Lighter Moments", "People & Influence", "What Life Taught Me"];

// Customer-facing 3-stage view mapped from internal statuses.
function stageFor(period) {
  if (!period.chapter) return "Gathering memories";
  if (period.chapter.status === "approved") return "Finished";
  return "Chapter draft ready";
}

function BigStat({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-serif text-3xl sm:text-4xl text-cream">{value}</div>
      <div className="text-xs text-cream/70 mt-0.5 uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function MyStory() {
  const navigate = useNavigate();
  const [story, setStory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gapsRequested, setGapsRequested] = useState(false);
  const [genPeriod, setGenPeriod] = useState(null);
  const [suggestion, setSuggestion] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [memMap, setMemMap] = useState({});
  const [moveOpen, setMoveOpen] = useState(null);

  const load = async () => {
    const [s, p, b, ch, m] = await Promise.all([api.get("/story"), api.get("/progress"), api.get("/book"), api.get("/chapters"), api.get("/memories")]);
    setStory(s.data); setProgress(p.data); setSuggestion(b.data?.suggested_structure || []);
    setChapters((ch.data || []).slice().sort((a, c) => (a.order ?? 0) - (c.order ?? 0)));
    const map = {}; (m.data || []).forEach((mm) => { map[mm.id] = mm; }); setMemMap(map);
    setLoading(false);
  };

  const moveMemory = async (mid, period) => {
    try { await api.put(`/memories/${mid}`, { period }); toast.success(`Moved to your ${period.toLowerCase()} memories`); await load(); }
    catch { toast.error("Couldn't move it just now. Please try again."); }
  };

  const moveChapter = async (i, dir) => {
    const ids = chapters.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      const { data } = await api.post("/chapters/reorder", { ordered_ids: ids });
      setChapters(data);
    } catch { toast.error("Couldn't reorder just now. Please try again."); }
  };
  useEffect(() => {
    load();
  }, []);

  const findIdeas = async () => {
    setGapsLoading(true);
    try { const { data } = await api.get("/gaps"); setGaps(data); setGapsRequested(true); }
    catch { toast.error("Couldn't look for ideas just now. Please try again."); }
    finally { setGapsLoading(false); }
  };

  const generate = async (period, memory_ids) => {
    setGenPeriod(period);
    try {
      const { data } = await api.post("/chapters/generate", { title: period, memory_ids });
      toast.success("Draft chapter created from your memories");
      navigate(`/story/${data.id}`);
    } catch { toast.error("Couldn't create the chapter right now. Please try again."); }
    finally { setGenPeriod(null); }
  };

  const suggestStructure = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/book/suggest-structure");
      setSuggestion(data.suggested_structure || []);
      if (!data.suggested_structure?.length) toast.error("Couldn't build a plan just now. Please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add a few more memories first.");
    } finally { setSuggesting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  const words = progress?.words_written || 0;
  const totalStories = progress?.stories || 0;
  const totalMemories = progress?.memories || 0;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">My Book</h1>
          <p className="text-subtle mt-1">Your memories, gathering into chapters.</p>
        </div>
        <button data-testid="go-book-btn" onClick={() => navigate("/book")}
          className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors">
          <BookMarked size={16} /> Preview or download my book
        </button>
      </div>

      {/* Continue my story — the guided next step */}
      <ContinueMyStory />

      {/* Memoir journey */}
      <JourneyIndicator progress={progress} chapters={chapters.length} />

      {/* Book progress hero */}
      <div data-testid="book-progress" className="rounded-3xl bg-teal text-cream p-7 sm:p-9 relative overflow-hidden animate-fade-up">
        <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-sage/20 blur-3xl" />
        <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold relative">Your memoir so far</p>
        <div className="mt-3 flex items-baseline gap-2 relative">
          <span data-testid="book-words-written" className="font-serif text-5xl sm:text-6xl text-cream">{words.toLocaleString()}</span>
          <span className="text-cream/80">words of your memoir written</span>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-4 max-w-md relative">
          <BigStat testid="book-stat-stories" value={totalStories} label={totalStories === 1 ? "story" : "stories"} />
          <BigStat testid="book-stat-memories" value={totalMemories} label={totalMemories === 1 ? "memory" : "memories"} />
          <BigStat testid="book-stat-chapters" value={chapters.length} label={chapters.length === 1 ? "chapter draft" : "chapter drafts"} />
        </div>
      </div>

      {story.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-line">
          <BookOpen className="mx-auto text-sage" size={36} />
          <h3 className="font-serif text-2xl text-charcoal mt-4">Your chapters will appear here</h3>
          <p className="mt-2 text-subtle">Record a few memories and we'll start organising them into chapters.</p>
          <button onClick={() => navigate("/answer")} className="mt-6 inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-6 py-3 rounded-full font-semibold">Answer a question</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {story.map((s, i) => {
            const statusText = stageFor(s);
            return (
              <div key={s.period} data-testid={`story-period-${i}`} className="bg-white rounded-2xl border border-line p-6 animate-fade-up">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl text-charcoal">{s.period}</h3>
                    <p className="text-sm text-subtle mt-0.5">
                      {s.memory_count} {s.memory_count === 1 ? "memory" : "memories"}
                      {s.story_count > 0 && <> · {s.story_count} {s.story_count === 1 ? "story" : "stories"}</>}
                      {s.words_written > 0 && <> · {s.words_written.toLocaleString()} words</>}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-teal bg-sagelight px-2.5 py-1 rounded-full whitespace-nowrap">{statusText}</span>
                </div>
                <div className="mt-5">
                  {s.chapter ? (
                    (() => {
                      const chIds = s.chapter.source_memory_ids || [];
                      const newCount = (s.memory_ids || []).filter((id) => !chIds.includes(id)).length;
                      return (
                        <div className="space-y-3">
                          {newCount > 0 && (
                            <p data-testid={`new-memories-note-${i}`} className="text-xs font-medium text-coral bg-coral/10 rounded-lg px-3 py-2">
                              {newCount} new {newCount === 1 ? "memory is" : "memories are"} not in your book yet — update this chapter to include {newCount === 1 ? "it" : "them"}.
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button data-testid={`view-chapter-${i}`} onClick={() => navigate(`/story/${s.chapter.id}`)}
                              className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm">
                              Open chapter <ArrowRight size={15} />
                            </button>
                            {newCount > 0 && (
                              <button data-testid={`update-chapter-${i}`} onClick={() => generate(s.period, s.memory_ids)} disabled={genPeriod === s.period}
                                className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
                                {genPeriod === s.period ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
                                Update with new memories
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  ) : s.memory_count >= 1 ? (
                    <button data-testid={`generate-chapter-${i}`} onClick={() => generate(s.period, s.memory_ids)} disabled={genPeriod === s.period}
                      className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
                      {genPeriod === s.period ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
                      Create my chapter
                    </button>
                  ) : (
                    <p className="text-sm text-subtle">Needs more memories</p>
                  )}
                </div>
                {s.memory_count > 0 && (
                  <div className="mt-4 border-t border-line pt-3">
                    <button data-testid={`move-toggle-${i}`} onClick={() => setMoveOpen(moveOpen === s.period ? null : s.period)}
                      className="text-xs font-medium text-subtle hover:text-teal inline-flex items-center gap-1.5">
                      <ArrowRight size={12} /> Move a memory to another life stage
                    </button>
                    {moveOpen === s.period && (
                      <div data-testid={`move-list-${i}`} className="mt-3 space-y-2">
                        {(s.memory_ids || []).map((mid) => {
                          const m = memMap[mid];
                          if (!m) return null;
                          return (
                            <div key={mid} className="flex items-center gap-2">
                              <span className="flex-1 text-sm text-charcoal truncate" title={m.question_text}>{m.question_text}</span>
                              <select data-testid={`move-select-${mid}`} value={s.period} onChange={(e) => moveMemory(mid, e.target.value)}
                                className="text-xs rounded-full border border-line bg-white px-2 py-1 focus:border-teal outline-none shrink-0">
                                {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reorder chapters */}
      {chapters.length > 1 && (
        <div data-testid="chapter-order" className="bg-white rounded-3xl border border-line p-6 sm:p-8 animate-fade-up">
          <h2 className="font-serif text-2xl text-charcoal">The order of your chapters</h2>
          <p className="text-subtle mt-1 text-sm">This is the order they'll appear in your finished book. Use the arrows to move a chapter up or down.</p>
          <div className="mt-5 space-y-3">
            {chapters.map((c, i) => (
              <div key={c.id} data-testid={`order-chapter-${i}`} className="flex items-center gap-3 bg-cream2 rounded-2xl border border-line p-4">
                <div className="flex flex-col">
                  <button data-testid={`order-up-${i}`} onClick={() => moveChapter(i, -1)} disabled={i === 0}
                    className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move up"><ChevronUp size={18} /></button>
                  <button data-testid={`order-down-${i}`} onClick={() => moveChapter(i, 1)} disabled={i === chapters.length - 1}
                    className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move down"><ChevronDown size={18} /></button>
                </div>
                <span className="font-serif text-teal text-lg w-6 shrink-0 text-center">{i + 1}</span>
                <span className="flex-1 font-serif text-lg text-charcoal">{c.title}</span>
                <button data-testid={`order-open-${i}`} onClick={() => navigate(`/story/${c.id}`)} className="text-sm font-semibold text-teal hover:underline shrink-0">Open</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggested structure — a personalised book plan */}
      {story.length > 0 && (
        <div data-testid="suggested-structure" className="bg-white rounded-3xl border border-line p-6 sm:p-8 animate-fade-up">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <h2 className="font-serif text-2xl text-charcoal">A chapter plan shaped around your life</h2>
              <p className="text-subtle mt-1 text-sm">A suggested outline, drawn only from the memories you've shared. It's an idea to inspire you — your own chapters stay exactly as they are.</p>
            </div>
            <button data-testid="suggest-structure-btn" onClick={suggestStructure} disabled={suggesting}
              className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
              {suggesting ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              {suggestion.length ? "Refresh plan" : "Suggest a plan"}
            </button>
          </div>
          {suggestion.length > 0 && (
            <ol className="mt-6 space-y-4">
              {suggestion.map((c, i) => (
                <li key={`${c.title || "chapter"}-${i}`} data-testid={`suggested-chapter-${i}`} className="flex gap-4">
                  <span className="font-serif text-teal text-xl leading-none mt-1 w-6 shrink-0">{i + 1}</span>
                  <div>
                    <p className="font-serif text-lg text-charcoal">{c.title}</p>
                    <p className="text-subtle text-sm mt-0.5 leading-relaxed">{c.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Story gaps */}
      <div>
        <h2 className="font-serif text-2xl text-charcoal">Gentle suggestions</h2>
        <p className="text-subtle mt-1 text-sm">A few threads you might like to explore next, drawn from what you've shared.</p>
        {!gapsRequested ? (
          <button data-testid="find-ideas-btn" onClick={findIdeas} disabled={gapsLoading}
            className="mt-4 inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
            {gapsLoading ? <><Loader2 className="animate-spin" size={16} /> Finding ideas…</> : <><Sparkles size={16} /> Find ideas for what to write next</>}
          </button>
        ) : gaps.length === 0 ? (
          <p className="mt-4 text-subtle text-sm">Nothing to suggest yet — keep sharing and we'll spot threads to follow.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {gaps.map((g, i) => (
              <div key={g.suggested_question || i} data-testid={`gap-${i}`} className="bg-white rounded-2xl border border-line p-5 flex items-start gap-4 animate-fade-up">
                <div className="text-sage mt-0.5"><HelpCircle size={20} /></div>
                <div className="flex-1">
                  <p className="text-charcoal">{g.description}</p>
                  <p className="text-teal font-medium mt-1">"{g.suggested_question}"</p>
                  <button
                    data-testid={`answer-gap-${i}`}
                    onClick={() => navigate("/answer", { state: { question: { id: null, text: g.suggested_question } } })}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:gap-2.5 transition-all">
                    <Feather size={15} /> Answer this <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
